/* eslint-disable global-require */
import 'normalize.css';

import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React, { FC, Suspense, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import ExternalLinkDialog from '~/common/ExternalLinkDialog';
import Seo from '~/common/Seo';
import BasicHeaderWrap from '~/components/BasicHeaderWrap';
import BasicHeaderWrapWithProfile from '~/components/BasicHeaderWrapWithProfile';
import { Footer } from '~/components/HCP/Footer/Footer';
import { mapFooterData } from '~/components/HCP/Footer/mapper';
import ProfessionConfirmationDialog from '~/components/HCP/ProfessionConfirmationDialog/';
import RenderOnHome from '~/components/HCP/RenderOnHome/RenderOnHome';
import { mapDataToSearchWithChipsProps } from '~/components/HCP/SearchWithChips/mapper';
import SearchWithChips from '~/components/HCP/SearchWithChips/SearchWithChips';
import HeaderWrap from '~/components/HeaderWrap';
import HeaderWrapWithProfile from '~/components/HeaderWrapWithProfile';
import { PageHeader } from '~/components/PageHeader';
import TopBar from '~/components/TopBar';
import { ExternalLinkDialogContext } from '~/context/ExternalLinkDialogContext';
import { SiteSettingsProvider } from '~/context/SiteSettingsContext';
import { TranslationsProvider } from '~/context/TranslationContext/TranslationsContext';
import { useContentDirection } from '~/hooks/useContentDirection';
import { useCurrentLang } from '~/hooks/useCurrentLang';
import { useLanguageSwitch } from '~/hooks/useLanguageSwitch';
import { infoBarMapper } from '~/mappers/infoBar/infoBarMapper';
import { pageHeaderMapper } from '~/mappers/pageHeader';
import { shouldLoadAuth } from '~/utils/importAuth';
import { ModalProvider } from '~/context/ModalContext';

import { LayoutProps } from './models';

if (process.env.TOKENS_FEATURE_FLAG === 'true')
  require('~/styles/main.new.scss');
else require('~/styles/main.scss');

// eslint-disable-next-line max-statements
export const Layout: FC<LayoutProps> = ({
  translations,
  extendedHeaderBasic,
  headerBasic,
  children,
  className,
  seo,
  disabledLayoutBackground,
  headerWithSplitter,
  headerSplitterAdditionalData,
  footerBasic,
  footerWithSplitter,
  pageUid,
  locale,
  brand,
  pageHeaderProps,
  infoBar,
  fullPath,
  openGraph,
  mainImagePath,
  profile,
  eventPresenters,
  categories,
  searchWithChips,
}) => {
  const CSSclassNames = classNames('layout', className);
  const { setCurrentLang } = useContext(ExternalLinkDialogContext);
  const { currentLang } = useCurrentLang();

  useEffect(() => {
    setCurrentLang(currentLang);
  }, [currentLang, setCurrentLang]);

  const languageSwitch = useLanguageSwitch(brand);
  const contentDirection = useContentDirection();

  const isAuth = shouldLoadAuth() && profile;
  const isBasicHeader = !isEmpty(headerBasic);

  const renderHeader = () => {
    if (isBasicHeader) {
      return isAuth ? (
        <div
          className={classNames({
            'basic-header': !disabledLayoutBackground,
          })}
        >
          <BasicHeaderWrapWithProfile
            headerBasic={headerBasic}
            headerWithSplitter={headerWithSplitter}
            headerSplitterAdditionalData={headerSplitterAdditionalData}
            languageSwitch={languageSwitch}
            profile={profile}
          />
        </div>
      ) : (
        <div
          className={classNames({
            'basic-header': !disabledLayoutBackground,
          })}
        >
          <BasicHeaderWrap
            headerBasic={headerBasic}
            headerWithSplitter={headerWithSplitter}
            headerSplitterAdditionalData={headerSplitterAdditionalData}
            languageSwitch={languageSwitch}
          />
        </div>
      );
    }

    return isAuth ? (
      <div className="extended-header">
        <HeaderWrapWithProfile
          extendedHeaderBasic={extendedHeaderBasic}
          profile={profile}
        />
      </div>
    ) : (
      <div className="extended-header">
        <HeaderWrap extendedHeaderBasic={extendedHeaderBasic} />
      </div>
    );
  };

  const layoutContent = (
    <Suspense fallback={null}>
      <ExternalLinkDialog />
      <ProfessionConfirmationDialog />
      <div className={CSSclassNames}>
        <div
          className={classNames({
            'layout-background': !disabledLayoutBackground,
          })}
        />
        <Helmet htmlAttributes={{ dir: contentDirection }} />
        {seo ? (
          <Seo
            {...{
              seo,
              urls: languageSwitch.list,
              pageUid,
              locale,
              fullPath,
              openGraph,
              mainImagePath,
            }}
          />
        ) : null}
        {renderHeader()}
        {infoBar && <TopBar {...infoBarMapper({ ...infoBar })} />}
        <main id="main" className="layout__main-wrapper">
          {!!pageHeaderProps?.pageHeader?.headingBlock?.headline ||
          pageHeaderProps?.pageHeader?.showBreadcrumbs ? (
            <PageHeader
              disabledLayoutBackground={disabledLayoutBackground}
              categories={categories}
              presenters={eventPresenters}
              {...pageHeaderMapper({ ...pageHeaderProps })}
            />
          ) : null}
          {searchWithChips && (
            <RenderOnHome>
              <div className="search__container">
                <SearchWithChips
                  {...mapDataToSearchWithChipsProps(searchWithChips)}
                />
              </div>
            </RenderOnHome>
          )}
          <div className="layout__main">{children}</div>
        </main>
        {footerWithSplitter || footerBasic ? (
          <Footer {...mapFooterData(footerBasic)} />
        ) : null}
      </div>
    </Suspense>
  );

  const contentWithTranslations = translations ? (
    <TranslationsProvider value={translations}>
      {layoutContent}
    </TranslationsProvider>
  ) : (
    layoutContent
  );

  return (
    <SiteSettingsProvider>
      <ModalProvider>{contentWithTranslations}</ModalProvider>
    </SiteSettingsProvider>
  );
};
